"use client"

import { useState, useTransition } from "react"
import Link from "next/link"
import { generateTriviaCards, type TriviaCard } from "@/app/generator/actions"
import { RefreshCw, Share2, Check } from "lucide-react"

const typeColors = {
  comparison: { bg: "bg-rose-500/10", border: "border-rose-500/20", text: "text-rose-400", label: "VS" },
  ranking: { bg: "bg-amber-500/10", border: "border-amber-500/20", text: "text-amber-400", label: "RANK" },
  fact: { bg: "bg-emerald-500/10", border: "border-emerald-500/20", text: "text-emerald-400", label: "FACT" },
  gap: { bg: "bg-cyan-500/10", border: "border-cyan-500/20", text: "text-cyan-400", label: "GAP" },
}

export function TriviaGenerator({ initialCards }: { initialCards: TriviaCard[] }) {
  const [cards, setCards] = useState(initialCards)
  const [isPending, startTransition] = useTransition()
  const [error, setError] = useState("")
  const [revealed, setRevealed] = useState<Set<number>>(new Set())
  const [copied, setCopied] = useState(false)

  const regenerate = () => {
    setError("")
    startTransition(async () => {
      try {
        const nextCards = await generateTriviaCards()
        if (!nextCards.length) {
          setError("Trivia is temporarily unavailable. Please try again.")
          return
        }
        setCards(nextCards)
        setRevealed(new Set())
        setCopied(false)
      } catch {
        setError("Could not load more questions. Please try again.")
      }
    })
  }

  const toggleReveal = (idx: number) => {
    setRevealed((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }

  const shareAll = async () => {
    const text = cards
      .map((c, i) => `Q${i + 1}: ${c.question}\nA: ${c.answer}`)
      .join("\n\n")
    const shareText = `${text}\n\nGenerated at playhigherlower.com/generator`

    if (navigator.share) {
      try {
        await navigator.share({ title: "Trivia Questions", text: shareText })
        return
      } catch (cause) {
        if (cause instanceof DOMException && cause.name === "AbortError") return
      }
    }
    try {
      await navigator.clipboard.writeText(shareText)
      setCopied(true)
      setError("")
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setError("Could not copy the questions. Please allow clipboard access and try again.")
    }
  }

  return (
    <div className="space-y-4">
      {!cards.length && <p className="text-zinc-400">Trivia is temporarily unavailable. Please try again.</p>}
      {error && <p role="alert" className="text-rose-300">{error}</p>}
      {cards.map((card, i) => {
        const color = typeColors[card.type]
        const isRevealed = revealed.has(i)
        return (
          <div key={i} className={`rounded-2xl ${color.bg} border ${color.border} overflow-hidden`}>
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <span className={`px-2 py-0.5 rounded text-xs font-bold ${color.text} ${color.bg}`}>{color.label}</span>
                <span className="text-xs text-zinc-500">{card.category}</span>
              </div>
              <p className="text-white font-medium text-lg mb-3">{card.question}</p>
              <button
                onClick={() => toggleReveal(i)}
                className={`w-full text-left p-3 rounded-xl transition-all ${
                  isRevealed
                    ? "bg-zinc-900/80 border border-zinc-700"
                    : "bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 cursor-pointer"
                }`}
              >
                {isRevealed ? (
                  <p className="text-sm text-zinc-300">{card.answer}</p>
                ) : (
                  <p className="text-sm text-zinc-500 italic">Tap to reveal answer</p>
                )}
              </button>
            </div>
          </div>
        )
      })}

      <div className="flex gap-3">
        <button
          onClick={regenerate}
          disabled={isPending}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white font-medium hover:bg-zinc-700 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          {isPending ? "Generating…" : "Generate more"}
        </button>
        <button
          onClick={shareAll}
          disabled={!cards.length || isPending}
          className={`flex items-center justify-center gap-2 px-5 py-3 rounded-xl border font-medium transition-all ${
            copied
              ? "bg-emerald-500/20 border-emerald-500/30 text-emerald-400"
              : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:text-white"
          }`}
        >
          {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
          {copied ? "Copied" : "Share"}
        </button>
      </div>

      <div className="text-center">
        <Link href="/play" className="text-sm text-zinc-500 hover:text-white transition-colors">
          Think you know the answers? Play the game →
        </Link>
      </div>
    </div>
  )
}
